import { absoluteValue, times } from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { asterAccountCategory } from '../constants'
import { createClient } from '../createClient'
import {
  type FuturesTrade,
  type SpotTrade,
  futuresResponseSchema,
  spotResponseSchema,
} from './schema'

/**
 * Aster の取引履歴を取得して Action に変換する。
 * signer address が設定されている場合は v3 を利用し、未設定の場合は v1 を利用する。
 * @param params - 取得条件
 * @returns 取引履歴から生成した Action 一覧
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiPassphrase,
  apiSecret,
  signAsterV3,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const useV3 = apiPassphrase !== undefined && apiPassphrase.length > 0
  if (useV3) {
    if (!signAsterV3) {
      throw new Error('signAsterV3 is required for Aster v3')
    }
  } else if (!apiSecret) {
    throw new Error('apiSecret is required for Aster v1')
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiPassphrase,
    apiSecret,
    signV3: signAsterV3,
    createHeaders,
    proxy,
  })

  const spotPath = useV3 ? '/api/v3/userTrades' : '/api/v1/userTrades'
  const futuresPath = useV3 ? '/fapi/v3/userTrades' : '/fapi/v1/userTrades'

  // Spot取引履歴を取得
  const spotTrades = await client.run({
    method: 'GET',
    path: spotPath,
    query: {
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
      limit: '1000',
    },
    responseSchema: spotResponseSchema,
  })

  // Futures取引履歴を取得
  const futuresTrades = await client.run({
    method: 'GET',
    path: futuresPath,
    query: {
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
      limit: '1000',
    },
    responseSchema: futuresResponseSchema,
  })

  // 全取引を統合してソート
  const allTrades = [
    ...spotTrades.map((t) => ({ ...t, type: 'spot' as const })),
    ...futuresTrades.map((t) => ({ ...t, type: 'futures' as const })),
  ].sort((a, b) => a.time - b.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const trade of allTrades) {
    fetchedFrom =
      fetchedFrom === undefined ? trade.time : Math.min(fetchedFrom, trade.time)
    fetchedTo =
      fetchedTo === undefined ? trade.time : Math.max(fetchedTo, trade.time)

    if (trade.type === 'spot') {
      // Spot取引の処理
      const spotTrade = trade as SpotTrade & { type: 'spot' }
      const [base, quote] = spotTrade.symbol.includes('/')
        ? spotTrade.symbol.split('/')
        : [spotTrade.symbol.replace(/USDT$|USDC$/, ''), 'USDT']

      const baseAsset = getAsset(base)
      const quoteAsset = getAsset(quote)
      const feeAsset = getAsset(spotTrade.commissionAsset)

      if (!baseAsset) {
        throw new AssetNotFoundException(base)
      }
      if (!quoteAsset) {
        throw new AssetNotFoundException(quote)
      }
      if (!feeAsset) {
        throw new AssetNotFoundException(spotTrade.commissionAsset)
      }

      const transfers: Transfer[] = []

      // 買いの場合: baseを受け取り、quoteを支払う
      if (spotTrade.side === 'BUY') {
        transfers.push({
          asset: baseAsset,
          amount: spotTrade.qty,
          direction: 'in',
        })
        transfers.push({
          asset: quoteAsset,
          amount: times(spotTrade.qty, spotTrade.price),
          direction: 'out',
        })
      } else {
        // 売りの場合: baseを支払い、quoteを受け取る
        transfers.push({
          asset: baseAsset,
          amount: spotTrade.qty,
          direction: 'out',
        })
        transfers.push({
          asset: quoteAsset,
          amount: times(spotTrade.qty, spotTrade.price),
          direction: 'in',
        })
      }

      // 手数料
      if (spotTrade.commission.value !== 0n) {
        transfers.push({
          asset: feeAsset,
          amount: absoluteValue(spotTrade.commission),
          direction: spotTrade.commission.value > 0n ? 'out' : 'in',
        })
      }

      actions.push({
        comment: `Spot Trade ID: ${spotTrade.id}`,
        evidence: 'system-rule',
        order: 0,
        source: `${asterAccountCategory}_${config.type}_spot_${spotTrade.id}_${spotTrade.time}`,
        type: 'trade',
        timestamp: spotTrade.time,
        transfers,
      })
    } else {
      // Futures取引の処理
      const futuresTrade = trade as FuturesTrade & { type: 'futures' }

      // 実現損益がある場合
      if (futuresTrade.realizedPnl && futuresTrade.realizedPnl.value !== 0n) {
        const [base] = futuresTrade.symbol.includes('/')
          ? futuresTrade.symbol.split('/')
          : [futuresTrade.symbol.replace(/USDT$|USDC$/, ''), 'USDT']
        const quote = futuresTrade.symbol.includes('/')
          ? futuresTrade.symbol.split('/')[1]
          : 'USDT'

        const quoteAsset = getAsset(quote)
        if (!quoteAsset) {
          throw new AssetNotFoundException(quote)
        }

        actions.push({
          comment: `Realized PL of ${base}/${quote}`,
          evidence: 'system-rule',
          order: 0,
          source: `${asterAccountCategory}_${config.type}_futures_pnl_${futuresTrade.id}_${futuresTrade.time}`,
          type:
            futuresTrade.realizedPnl.value > 0n
              ? 'realize-profit'
              : 'realize-loss',
          timestamp: futuresTrade.time,
          transfers: [
            {
              asset: quoteAsset,
              amount: absoluteValue(futuresTrade.realizedPnl),
              direction: futuresTrade.realizedPnl.value > 0n ? 'in' : 'out',
            },
          ],
        })
      }

      // 手数料がある場合
      if (futuresTrade.commission.value !== 0n) {
        const feeAsset = getAsset(futuresTrade.commissionAsset)
        if (!feeAsset) {
          throw new AssetNotFoundException(futuresTrade.commissionAsset)
        }

        const [base] = futuresTrade.symbol.includes('/')
          ? futuresTrade.symbol.split('/')
          : [futuresTrade.symbol.replace(/USDT$|USDC$/, ''), 'USDT']
        const quote = futuresTrade.symbol.includes('/')
          ? futuresTrade.symbol.split('/')[1]
          : 'USDT'

        actions.push({
          comment: `Trade Fee of ${base}/${quote}`,
          evidence: 'system-rule',
          order: 0,
          source: `${asterAccountCategory}_${config.type}_futures_fee_${futuresTrade.id}_${futuresTrade.time}`,
          type: futuresTrade.commission.value > 0n ? 'fee' : 'income',
          timestamp: futuresTrade.time,
          transfers: [
            {
              asset: feeAsset,
              amount: absoluteValue(futuresTrade.commission),
              direction: futuresTrade.commission.value > 0n ? 'out' : 'in',
            },
          ],
        })
      }
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: allTrades.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
